import { Film } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useThumbnailSrc } from '../../lib/useProxyImage';
import { PlayBadge } from '../media/MediaThumb';
import type { ThreadPreviewPicture } from './threadPreviewPosts';

/** One picture of a post: an image, or the still frame that stands for its video. */
export type PreviewPicture = ThreadPreviewPicture;

/**
 * A post's pictures, the same in the thread preview and in the single post's: one across the whole
 * width, two or more in two columns of square cells - three as two side by side and the third across
 * both. One frame for every count, so that a picture stays where it is, and keeps what it has
 * loaded, when another is added beside it.
 *
 * `pictures` are the ones SHOWN - at most `THREAD_PREVIEW_MAX_CELLS` - and `more` how many images
 * the post holds beyond them: printed over the last cell as "+N".
 */
export function PreviewImageGrid({
  pictures,
  more,
  className,
}: {
  pictures: PreviewPicture[];
  more: number;
  /** The frame around the pictures, which differs between the two previews. */
  className?: string;
}) {
  if (pictures.length === 0) return null;
  const last = pictures.length - 1;
  return (
    // theme-light: the pictures of a post preview, which stays light in both themes.
    <div className={cn('theme-light', className, pictures.length > 1 && 'grid grid-cols-2 gap-1')}>
      {pictures.map((picture, i) => (
        <PreviewImageCell
          key={picture.key}
          picture={picture}
          wide={pictures.length === 3 && i === last}
          // The last cell stands for the images there is no cell for.
          more={i === last ? more : 0}
        />
      ))}
    </div>
  );
}

/**
 * One picture, in a component of its own so that it loads on its own: each asks for its own file,
 * and - keyed by the block it came from - keeps it while it, or its post, moves.
 */
function PreviewImageCell({
  picture,
  wide,
  more,
}: {
  picture: PreviewPicture;
  /** The third of three: across both columns, at the height of the two above it. */
  wide: boolean;
  /** How many more images the post holds; printed over this one when there are any. */
  more: number;
}) {
  // The small copy, or a video's still frame - fetched with the app's token when it is the app's
  // own file, and used as it is when it is hosted elsewhere.
  const src = useThumbnailSrc(picture.url);
  return (
    <div
      className={cn(
        // Holds its place, in a muted tone, until its picture arrives.
        'relative overflow-hidden bg-chip text-ink-ghost',
        wide ? 'col-span-2 aspect-[2/1]' : 'aspect-square',
      )}
    >
      {src ? (
        <>
          <img src={src} alt="" className="block h-full w-full object-cover" />
          {picture.video && <PlayBadge />}
        </>
      ) : (
        picture.video && (
          // A video with no still frame (yet): the film icon says "video" by itself, as it does in
          // the composer's tiles - a play mark is only ever laid over a picture.
          <span className="flex h-full w-full items-center justify-center">
            <Film className="h-8 w-8" aria-hidden="true" />
          </span>
        )
      )}
      {more > 0 && (
        <span className="absolute inset-0 flex items-center justify-center bg-black/60 text-base font-semibold text-white">
          +{more}
          <span className="sr-only"> more images</span>
        </span>
      )}
    </div>
  );
}
